import { reactive, watch } from "vue";
import { getDatabase, ref, onValue } from "firebase/database";
import { authStore } from "@/store/auth";
import clientFlagNames from "./clientFlags.json";

// Runtime feature flags. Every flag is declared in featureFlags.config.json
// (repo root); clientFlags.json is the generated list of its `client: true`
// names (run `npm run sync:shared-functions` after editing the registry —
// importing the registry here directly would ship backend-only flag names in
// the public bundle). Values live in RTDB under feature-flags/{flag}
// ({all, users}) and are toggled from the admin panel — no redeploy needed.
// A flag is on when its `all` switch or the signed-in user's allowlist entry
// is true; reads fail closed.
//
// Components keep gating with `v-if="featureFlags.<name>"`. Flags start
// false and can flip true moments after load, so one-shot startup checks
// must watch the flag instead (see App.vue's push init).

export const featureFlags = reactive(
  Object.fromEntries(clientFlagNames.map((name) => [name, false]))
);

const allEnabled = {};
const userEnabled = {};
let initialized = false;
let allUnsubs = [];
let userUnsubs = [];
let stopUserWatch = null;

// --- Readiness ------------------------------------------------------------
//
// A flag is false until its listeners answer. That is right for rendering: a
// surface that has not heard back yet must not show a gated feature. It is
// wrong for the one decision that is made once and cannot be re-made, the
// route the app lands on after sign-in, which needs to tell "the store has
// heard back and this learner is not allowlisted" apart from "the store has
// not heard yet".
//
// `allReported` accumulates for the life of the process, because the `all`
// listeners are created once by initFeatureFlags and are never re-subscribed.
// `userReported` is emptied on every auth-state change, because the per-user
// listeners are torn down and re-subscribed for the new uid, and `readyUid`
// records which uid the entries in it belong to, so evidence gathered for one
// account can never settle a wait registered for another.
//
// A waiter is keyed on the uid it asked about rather than handed whichever
// promise happened to be current. On the two paths that await sign-in directly
// the uid is already in authStore by the time landingRoute() runs, because the
// SDK notifies its auth observers before signInWithPopup and
// signInWithEmailAndPassword resolve. The keying is for the paths where that
// ordering is NOT guaranteed: the per-user listeners below are re-subscribed
// from a Vue `watch`, which flushes on its own schedule, and both setupAuth and
// checkForRedirectAuth decide a destination from inside an auth listener. A
// keyed waiter simply stays pending until that uid's own listeners report,
// whichever order the two arrive in.
const allReported = new Set();
let userReported = new Set();
let readyUid = null;
let waiters = [];

function isReadyFor(uid) {
  return (
    allReported.size === clientFlagNames.length &&
    userReported.size === clientFlagNames.length &&
    readyUid === uid
  );
}

// How long an unsettled waiter is kept before it is dropped.
//
// landingRoute() races this promise against its own 1500 ms bound and then
// walks away from it, so a uid whose listeners never report would leave an
// entry that flushWaiters re-scans for the life of the session. A dropped
// waiter is dropped rather than resolved: resolving it would claim the flags
// had reported when they had not, and the only caller already bounds its own
// wait, so a promise that never settles costs it nothing.
const WAITER_TTL_MS = 60000;

function flushWaiters() {
  if (!waiters.length) return;
  const cutoff = Date.now() - WAITER_TTL_MS;
  const stillWaiting = [];
  for (const waiter of waiters) {
    if (isReadyFor(waiter.uid)) waiter.resolve();
    else if (waiter.addedAt >= cutoff) stillWaiting.push(waiter);
  }
  waiters = stillWaiting;
}

/**
 * Resolves once both the `all` value and `uid`'s allowlist entry have reported
 * for every client flag, so `featureFlags` holds this learner's real values
 * rather than the fail-closed defaults.
 *
 * It never rejects and carries no timeout of its own. A caller that cannot
 * wait forever imposes its own bound; `src/scripts/landingRoute.js` is the one
 * caller and bounds it at LANDING_FLAG_WAIT_MS. If initFeatureFlags() was
 * never called nothing ever reports and this stays pending, which is exactly
 * what that bound exists for.
 *
 * @param {string|null} uid the signed-in uid, or null while signed out.
 * @returns {Promise<void>}
 */
export function whenFeatureFlagsReady(uid = null) {
  const target = uid ?? null;
  if (isReadyFor(target)) return Promise.resolve();
  let entry = null;
  const promise = new Promise((resolve) => {
    entry = { uid: target, resolve, addedAt: Date.now() };
    waiters.push(entry);
  });
  // A caller that walks away from this promise can say so, which is the only
  // reliable way to drop the waiter: the TTL above is swept by flushWaiters,
  // and flushWaiters only runs when a listener reports — the very thing that is
  // not happening in the case the sweep is meant to clean up after.
  promise.cancel = () => {
    waiters = waiters.filter((waiter) => waiter !== entry);
  };
  return promise;
}

function recompute(name) {
  featureFlags[name] = allEnabled[name] === true || userEnabled[name] === true;
}

export function initFeatureFlags() {
  if (initialized) return;
  initialized = true;
  const db = getDatabase();

  for (const name of clientFlagNames) {
    allUnsubs.push(
      onValue(
        ref(db, `feature-flags/${name}/all`),
        (snap) => {
          allEnabled[name] = snap.val() === true;
          recompute(name);
          allReported.add(name);
          flushWaiters();
        },
        // A denied or failed read is still an answer: the flag is off and
        // stays off, so readiness must not hang on it.
        () => {
          allEnabled[name] = false;
          recompute(name);
          allReported.add(name);
          flushWaiters();
        }
      )
    );
  }

  // Per-user listeners follow the signed-in uid: none while logged out,
  // re-subscribed on every sign-in/sign-out.
  stopUserWatch = watch(
    () => authStore.userId,
    (userId) => {
      while (userUnsubs.length) userUnsubs.pop()();
      // The previous account's allowlist evidence is discarded here, not
      // merely superseded: it describes a different learner.
      userReported = new Set();
      readyUid = userId ?? null;
      for (const name of clientFlagNames) {
        userEnabled[name] = false;
        recompute(name);
      }
      if (!userId) {
        // Signed out there are no per-user listeners to wait on, so the user
        // half has already reported for every flag: each one is false, and no
        // later snapshot can change that while nobody is signed in.
        for (const name of clientFlagNames) userReported.add(name);
        flushWaiters();
        return;
      }
      for (const name of clientFlagNames) {
        userUnsubs.push(
          onValue(
            ref(db, `feature-flags/${name}/users/${userId}`),
            (snap) => {
              userEnabled[name] = snap.val() === true;
              recompute(name);
              if (readyUid === userId) {
                userReported.add(name);
                flushWaiters();
              }
            },
            () => {
              userEnabled[name] = false;
              recompute(name);
              if (readyUid === userId) {
                userReported.add(name);
                flushWaiters();
              }
            }
          )
        );
      }
    },
    { immediate: true }
  );
}

// Test-only: how many waits are still outstanding. Exposed so a test can prove
// an abandoned waiter was actually dropped rather than merely never resolved,
// which is not observable from outside any other way.
export function _featureFlagWaiterCountForTests() {
  return waiters.length;
}

// Test-only: tear down every subscription and reset state so each test can
// call initFeatureFlags() against a fresh mock.
export function _resetFeatureFlagsForTests() {
  while (allUnsubs.length) allUnsubs.pop()();
  while (userUnsubs.length) userUnsubs.pop()();
  if (stopUserWatch) stopUserWatch();
  stopUserWatch = null;
  initialized = false;
  allReported.clear();
  userReported = new Set();
  readyUid = null;
  waiters = [];
  for (const name of clientFlagNames) {
    allEnabled[name] = false;
    userEnabled[name] = false;
    featureFlags[name] = false;
  }
}
